/**
 * CDM Software Inventory — the four-level vendor → product → versions →
 * installations graph as a flat tree. Powers Software Asset Management.
 *
 * Phase 1 ships the shape; rich data lights up as Intune (Phase 3) and
 * vuln scanners (Phase 5) populate cdm_software_installations with
 * canonical CPEs.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Layers, RefreshCw, ChevronLeft, Search, Filter, AlertTriangle,
  Package,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SoftwareRow {
  vendor: string
  product: string
  versions: string[]
  install_count: number
  eol_versions: string[]
  affected_by_cve_count: number
}

interface VendorConcRow {
  vendor: string
  asset_count: number
  user_count: number
  install_count: number
  cve_open_count: number
}

export default function CdmSoftwarePage() {
  const [rows, setRows] = useState<SoftwareRow[]>([])
  const [vendorConc, setVendorConc] = useState<VendorConcRow[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/cdm/software', { params: { vendor: vendorFilter || undefined } }),
      api.get('/api/cdm/vendor-concentration', { params: { top_n: 20 } }),
    ])
      .then(([sw, vc]) => {
        setRows(sw.data?.rows || [])
        setVendorConc(vc.data?.rows || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [vendorFilter])

  const vendors = useMemo(() => {
    const s = new Set(rows.map(r => r.vendor))
    return Array.from(s).sort()
  }, [rows])

  const filtered = useMemo(() =>
    rows.filter(r => !search ||
      r.vendor.toLowerCase().includes(search.toLowerCase()) ||
      r.product.toLowerCase().includes(search.toLowerCase())),
  [rows, search])

  const totalInstalls = filtered.reduce((s, r) => s + r.install_count, 0)
  const totalProducts = filtered.length
  const totalEolVersions = filtered.reduce((s, r) => s + r.eol_versions.length, 0)
  const totalCveAffected = filtered.reduce((s, r) => s + r.affected_by_cve_count, 0)

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Layers size={18} className="text-cyan" /> SOFTWARE INVENTORY
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          vendors → products → versions → installations · CPE / PURL canonical
        </span>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Products tracked" value={totalProducts} tone="text-cyan" />
        <Tile label="Installations" value={totalInstalls} tone="text-emerald" />
        <Tile label="EOL versions" value={totalEolVersions} tone="text-amber-light" warn={totalEolVersions > 0} />
        <Tile label="Affected by CVE" value={totalCveAffected} tone="text-rose-light" warn={totalCveAffected > 0} />
      </div>

      {/* Vendor concentration */}
      {vendorConc.length > 0 && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
            <Package size={14} /> Vendor concentration <span className="text-[10px] text-text-muted font-normal">— DORA / operational-resilience</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {vendorConc.slice(0, 20).map(v => (
              <button key={v.vendor} type="button"
                onClick={() => setVendorFilter(v.vendor === vendorFilter ? '' : v.vendor)}
                className={clsx(
                  'p-2 rounded border text-left',
                  v.vendor === vendorFilter ? 'border-cyan/60 bg-cyan/10' : 'border-border bg-surface-card hover:bg-surface-raised',
                )}>
                <div className="text-[10px] text-text-muted">{v.vendor}</div>
                <div className="text-sm font-bold text-text-primary">{v.install_count} installs</div>
                {v.cve_open_count > 0 && (
                  <div className="text-[10px] text-rose-light">{v.cve_open_count} open CVEs</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
                aria-label="Filter by vendor">
          <option value="">All vendors</option>
          {vendors.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <div className="relative flex-1 max-w-sm">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="search vendor or product"
            className="input pl-7 w-full" />
        </div>
        <span className="text-text-muted ml-auto">{filtered.length} products</span>
      </div>

      <div className="card p-0 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-surface-raised">
            <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2">Versions</th>
              <th className="text-right px-3 py-2">Installs</th>
              <th className="text-right px-3 py-2">EOL</th>
              <th className="text-right px-3 py-2">Affected (CVE)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-muted">
                No software inventory yet. Configure Intune (Phase 3) or vuln scanners
                (Phase 5) to populate the four-level CDM software graph.
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={i} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1.5 text-purple-light">{r.vendor}</td>
                <td className="px-3 py-1.5 font-semibold text-text-primary">{r.product}</td>
                <td className="px-3 py-1.5 text-text-secondary">
                  {r.versions.slice(0, 5).map(v => (
                    <span key={v} className="badge text-[9px] mr-1 bg-surface-raised text-text-muted border border-border">
                      {v}
                    </span>
                  ))}
                  {r.versions.length > 5 && (
                    <span className="text-[10px] text-text-muted">+{r.versions.length - 5}</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald">{r.install_count}</td>
                <td className="px-3 py-1.5 text-right font-mono text-amber-light">
                  {r.eol_versions.length || '—'}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {r.affected_by_cve_count > 0 ? (
                    <span className="text-rose-light inline-flex items-center gap-1">
                      <AlertTriangle size={10} /> {r.affected_by_cve_count}
                    </span>
                  ) : (
                    <span className="text-text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}


function Tile({ label, value, tone, warn }: { label: string; value: number; tone: string; warn?: boolean }) {
  return (
    <div className={clsx('card p-3', warn && 'ring-1 ring-amber/40')}>
      <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', tone)}>{value.toLocaleString()}</div>
    </div>
  )
}
